import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import MainMenu from '../Navigation/MainMenu/MainMenu';
import Countdown from './Countdown/Countdown';
import CampaignMetrics from '../CampaignMetrics/CampaignMetrics';
import HeroBackground from './HeroBackground';
import HeroOverlay from './HeroOverlay';
import styles from './Header.module.scss';
import {
    useNavMenuConfig,
    useKeyVisualConfig,
    useCountdownConfig,
    useMetricsConfig,
    formatCSSValue,
} from '../../store/designStore';

const Header = (props) => {
    const [groupHeights, setGroupHeights] = useState({});

    const onGroupHeightChange = useCallback((id, height) => {
        setGroupHeights((prev) => {
            if (prev[id] === height) return prev;
            const updated = { ...prev };
            if (height === 0) {
                delete updated[id];
            } else {
                updated[id] = height;
            }
            return updated;
        });
    }, []);

    const maxOverlayHeight = useMemo(() => {
        const heights = Object.values(groupHeights);
        if (heights.length === 0) return 0;
        const max = Math.max(...heights);
        return max > 0 ? max + 32 : 0; // 32px padding buffer (16px top/bottom)
    }, [groupHeights]);

    const { isFullWidth: isNavFullWidth, backgroundColor: navBackgroundColor } = useNavMenuConfig();
    const { isFullWidth: isKeyVisualFullWidth } = useKeyVisualConfig();
    const { isFullWidth: isCountdownFullWidth } = useCountdownConfig();
    const {
        detached: { desktop: detachedMetrics, mobile: detachedMetricsMobile } = {},
        offset: { value: metricsOffsetValue, unit: metricsOffsetUnit } = {},
        gap: { desktop: metricsGapValueDesktop, mobile: metricsGapValueMobile } = {},
        borderRadius: detachedMetricsBorderRadius,
    } = useMetricsConfig();
    const metricsRef = useRef(null);
    const { height: metricsHeight = 90 } = useResizeObserver({
        ref: metricsRef,
        box: 'border-box',
    });

    return (
        <header className="w-100" style={{ display: 'contents' }}>
            <div className={`w-100 sticky-top ${styles.countdownStickyWrapper}`}>
                <div className={isCountdownFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                    <Countdown />
                </div>
            </div>

            <div style={navBackgroundColor ? { backgroundColor: navBackgroundColor } : undefined}>
                <div className={isNavFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                    <MainMenu
                        primaryNavigation={props.primaryNavigation}
                        primaryNavigationCTA={props.primaryNavigationCTA}
                    />
                </div>
            </div>

            <div
                className={`position-relative w-100 ${
                    detachedMetrics
                        ? `${styles.headerVisualWrapperOverlay} ${detachedMetricsMobile ? styles.detachedMobile : ''}`
                        : ''
                }`}
                style={
                    detachedMetrics
                        ? {
                              '--metrics-margin-bottom':
                                  metricsOffsetUnit === 'px'
                                      ? `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}px + ${formatCSSValue(metricsGapValueDesktop) || '0px'}))`
                                      : metricsOffsetUnit === '%'
                                        ? `max(0px, calc(${metricsHeight * (1 + metricsOffsetValue / 100)}px + ${formatCSSValue(metricsGapValueDesktop) || '0px'}))`
                                        : `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${formatCSSValue(metricsGapValueDesktop) || '0px'}))`,
                              ...(detachedMetricsMobile
                                  ? {
                                        '--metrics-margin-bottom-mobile':
                                            metricsOffsetUnit === 'px'
                                                ? `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}px + ${formatCSSValue(metricsGapValueMobile) || '0px'}))`
                                                : metricsOffsetUnit === '%'
                                                  ? `max(0px, calc(${metricsHeight * (1 + metricsOffsetValue / 100)}px + ${formatCSSValue(metricsGapValueMobile) || '0px'}))`
                                                  : `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${formatCSSValue(metricsGapValueMobile) || '0px'}))`,
                                    }
                                  : {}),
                          }
                        : {}
                }
            >
                {/* Background Section (Solid Fill vs KeyVisual Image) */}
                <HeroBackground pageNotFound={props.pageNotFound} maxOverlayHeight={maxOverlayHeight} />

                {/* Overlaid Elements Grouped by Position */}
                {!props.pageNotFound && <HeroOverlay onGroupHeightChange={onGroupHeightChange} />}

                {/* Overlaid Campaign Metrics (Desktop only) */}
                {!props.pageNotFound && detachedMetrics && (
                    <div
                        ref={metricsRef}
                        className={`position-absolute bottom-0 start-50 w-100 ${
                            detachedMetricsMobile ? '' : 'd-none d-lg-block'
                        } ${styles.metricsOverlayContainer}`}
                        style={{
                            transform: `translate(-50%, calc(100% + ${metricsOffsetValue}${metricsOffsetUnit}))`,
                        }}
                    >
                        <div className="container-lg">
                            <div
                                className={styles.metricsOverlayCard}
                                style={
                                    detachedMetricsBorderRadius !== undefined
                                        ? {
                                              '--detached-metrics-border-radius':
                                                  formatCSSValue(detachedMetricsBorderRadius),
                                          }
                                        : {}
                                }
                            >
                                <CampaignMetrics />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {!detachedMetrics || props.pageNotFound ? (
                <div
                    className={`${isKeyVisualFullWidth ? 'container-fluid px-0' : 'container-lg px-0'} ${
                        styles.inflowMetricsWrapper
                    }`}
                    style={{
                        ...(metricsGapValueDesktop !== undefined
                            ? { '--inflow-margin-bottom-desktop': formatCSSValue(metricsGapValueDesktop) }
                            : {}),
                        ...(metricsGapValueMobile !== undefined
                            ? { '--inflow-margin-bottom-mobile': formatCSSValue(metricsGapValueMobile) }
                            : {}),
                    }}
                >
                    <CampaignMetrics />
                </div>
            ) : !detachedMetricsMobile ? (
                <div className="d-lg-none">
                    <div
                        className={`${isKeyVisualFullWidth ? 'container-fluid px-0' : 'container-lg px-0'} ${
                            styles.inflowMetricsWrapper
                        }`}
                        style={{
                            ...(metricsGapValueMobile !== undefined
                                ? { '--inflow-margin-bottom-mobile': formatCSSValue(metricsGapValueMobile) }
                                : {}),
                        }}
                    >
                        <CampaignMetrics />
                    </div>
                </div>
            ) : null}
        </header>
    );
};

export default Header;
